import type { PayrollJournalEntryRow, PayrollTab } from './types';

export function formatCurrency(value: number, compact = false): string {
  const amount = Number(value || 0);
  const sign = amount < 0 ? '-' : '';
  const abs = Math.abs(amount);

  if (compact && abs >= 1_000_000) {
    return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  }
  if (compact && abs >= 1_000) {
    return `${sign}$${Math.round(abs / 1_000).toLocaleString()}K`;
  }

  return `${sign}$${abs.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function formatPct(value: number): string {
  return `${Number(value || 0).toFixed(2)}%`;
}

export function calculateBalance(rows: PayrollJournalEntryRow[]) {
  const debitTotal = roundMoney(rows.reduce((sum, row) => sum + Number(row.debit || 0), 0));
  const creditTotal = roundMoney(rows.reduce((sum, row) => sum + Number(row.credit || 0), 0));
  const delta = roundMoney(debitTotal - creditTotal);
  return {
    debitTotal,
    creditTotal,
    delta,
    isBalanced: Math.abs(delta) < 0.02,
  };
}

export function buildNetSuiteCsv(rows: PayrollJournalEntryRow[]): string {
  const headers = [
    'External ID',
    'Date',
    'Memo',
    'Line',
    'Account',
    'Account Name',
    'Debit',
    'Credit',
    'Department',
    'Subsidiary',
    'Currency',
    'Line Memo',
  ];

  const csvRows = rows.map(row => [
    row.external_id,
    row.date,
    row.memo,
    String(row.line),
    row.account,
    row.account_name,
    row.debit ? row.debit.toFixed(2) : '',
    row.credit ? row.credit.toFixed(2) : '',
    row.department,
    row.subsidiary,
    row.currency,
    row.line_memo,
  ]);

  return [headers, ...csvRows].map(values => values.map(escapeCsv).join(',')).join('\n');
}

export function routeToTab(pathname: string): PayrollTab {
  if (pathname.endsWith('/journal-entry')) return 'journal-entry';
  if (pathname.endsWith('/headcount')) return 'headcount';
  if (pathname.endsWith('/burden')) return 'burden';
  if (pathname.endsWith('/audit')) return 'audit';
  if (pathname.endsWith('/source-files')) return 'source-files';
  if (pathname.endsWith('/configuration')) return 'configuration';
  return 'review';
}

export function tabToPath(tab: PayrollTab): string {
  if (tab === 'review') return '/payroll-entry';
  return `/payroll-entry/${tab}`;
}

export function safePeriodFileToken(period: string): string {
  return period.replace(/\s+/g, '').replace(/[^A-Za-z0-9]/g, '');
}

function escapeCsv(value: string): string {
  return `"${String(value ?? '').replace(/"/g, '""')}"`;
}

function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}
